import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Layout } from './components/Layout';
import { Toaster } from './components/ui/sonner';

// Pages
import Home from './pages/Home';
import Auth, { AuthCallback } from './pages/Auth';
import Cooperatives from './pages/Cooperatives';
import Terrassements from './pages/Terrassements';
import { SitesList, SiteDetail } from './pages/Sites';
import Terrains from './pages/Terrains';
import Logements from './pages/Logements';
import RendezVous from './pages/RendezVous';
import Visites from './pages/Visites';
import Admin from './pages/Admin';
import Contact from './pages/Contact';
import Profil from './pages/Profil';
import About from './pages/About';

// Protected Route Component
const ProtectedRoute = ({ children, roles = [] }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  if (roles.length > 0 && !roles.some(role => user.roles?.includes(role))) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// App Router with session_id detection
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (synchronous during render)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/cooperatives" element={<Cooperatives />} />
        <Route path="/terrassements" element={<Terrassements />} />
        <Route path="/sites" element={<SitesList />} />
        <Route path="/sites/:siteId" element={<SiteDetail />} />
        <Route path="/sites/:siteId/terrains" element={<Terrains />} />
        <Route path="/logements" element={<Logements />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/a-propos" element={<About />} />
        <Route 
          path="/profil" 
          element={
            <ProtectedRoute>
              <Profil />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/visites" 
          element={
            <ProtectedRoute roles={['ADMIN', 'SUPER_ADMIN', 'AGENT']}>
              <Visites />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/rendez-vous" 
          element={
            <ProtectedRoute roles={['ADMIN', 'SUPER_ADMIN', 'AGENT']}>
              <RendezVous />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin" 
          element={
            <ProtectedRoute roles={['ADMIN', 'SUPER_ADMIN']}>
              <Admin />
            </ProtectedRoute>
          } 
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
